import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { AuthService } from '../../../../core/services/auth.service';
import { SwalService } from '../../../../core/services/swal.service';
import { AngularPhoneNumberInput } from 'angular-phone-number-input';
import {
  LucideAngularModule,
  Loader2Icon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
} from 'lucide-angular';

@Component({
  selector: 'app-reset-password-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AngularPhoneNumberInput,
    LucideAngularModule,
  ],
  templateUrl: './reset-password-modal.component.html',
})
export class ResetPasswordModalComponent {
  readonly Loader2Icon = Loader2Icon;
  readonly EyeIcon = EyeIcon;
  readonly EyeOffIcon = EyeOffIcon;
  readonly LockIcon = LockIcon;

  // États du processus
  currentStep = 1; // 1: Demande OTP, 2: Vérification OTP, 3: Nouveau mot de passe
  otpId: string = '';
  phoneNumber: string = '';
  indicative: string = '';

  // Formulaires
  step1Form = new FormGroup({
    phoneNumber: new FormControl('', [Validators.required]),
  });

  step2Form = new FormGroup({
    otpCode: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
    ]),
  });

  step3Form = new FormGroup({
    newPassword: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.pattern(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/
      ),
    ]),
    confirmPassword: new FormControl('', [Validators.required]),
  });

  preferredCountries: string[] = ['SN', 'GA', 'CI'];
  phoneNumberData: any = null;
  isLoading = false;
  showPassword = false;
  showConfirmPassword = false;

  constructor(
    private modalService: ModalService,
    private authService: AuthService,
    private swalService: SwalService
  ) {}

  onPhoneNumberChange(event: any): void {
    this.phoneNumberData = event;
  }

  // Étape 1: Demande d'OTP
  onStep1Submit(): void {
    if (this.step1Form.valid) {
      this.isLoading = true;
      const formValue = this.step1Form.value;
      const fullPhoneNumber = formValue.phoneNumber || '';

      // Extraire l'indicatif et le numéro
      const { indicative, phoneNumber } =
        this.extractPhoneNumber(fullPhoneNumber);

      if (!indicative || !phoneNumber) {
        this.swalService.error(
          'Erreur',
          'Format de numéro de téléphone invalide'
        );
        this.isLoading = false;
        return;
      }

      this.phoneNumber = phoneNumber;
      this.indicative = indicative;

      const resetData = {
        mobileNumber: phoneNumber,
        indicative,
      };

      this.authService.requestPasswordReset(resetData).subscribe({
        next: (response) => {
          this.swalService.success(
            'Code envoyé',
            'Un code de vérification a été envoyé à votre numéro de téléphone.'
          );
          this.currentStep = 2;
          this.isLoading = false;
        },
        error: (error) => {
          this.swalService.error(
            'Erreur',
            error.error?.message ||
              "Une erreur est survenue lors de l'envoi du code."
          );
          this.isLoading = false;
        },
      });
    } else {
      this.step1Form.markAllAsTouched();
    }
  }

  // Étape 2: Vérification OTP
  onStep2Submit(): void {
    if (this.step2Form.valid) {
      this.isLoading = true;
      const formValue = this.step2Form.value;

      const checkData = {
        code: formValue.otpCode || '',
        phoneNumber: this.phoneNumber,
      };

      this.authService.checkPasswordOtp(checkData).subscribe({
        next: (response) => {
          this.otpId = response.otpId;
          this.swalService.success(
            'Code validé',
            'Code de vérification validé avec succès.'
          );
          this.currentStep = 3;
          this.isLoading = false;
        },
        error: (error) => {
          this.swalService.error(
            'Erreur',
            error.error?.message || 'Code de vérification invalide.'
          );
          this.isLoading = false;
        },
      });
    } else {
      this.step2Form.markAllAsTouched();
    }
  }

  // Étape 3: Changement de mot de passe
  onStep3Submit(): void {
    if (this.step3Form.valid) {
      const formValue = this.step3Form.value;

      if (formValue.newPassword !== formValue.confirmPassword) {
        this.swalService.error(
          'Erreur',
          'Les mots de passe ne correspondent pas.'
        );
        return;
      }

      this.isLoading = true;

      const changeData = {
        phoneNumber: this.phoneNumber,
        newPassword: formValue.newPassword || '',
        otpId: this.otpId,
      };

      this.authService.changePassword(changeData).subscribe({
        next: (response) => {
          this.swalService.success(
            'Succès',
            'Votre mot de passe a été modifié avec succès.'
          );
          this.modalService.closeAll();
        },
        error: (error) => {
          this.swalService.error(
            'Erreur',
            error.error?.message ||
              'Une erreur est survenue lors du changement de mot de passe.'
          );
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        },
      });
    } else {
      this.step3Form.markAllAsTouched();
    }
  }

  // Méthode pour extraire le numéro de téléphone
  private extractPhoneNumber(fullPhoneNumber: string): {
    indicative: string;
    phoneNumber: string;
  } {
    // Définir les indicatifs des pays préférés
    const countryCodes = {
      SN: '221', // Sénégal
      CI: '225', // Côte d'Ivoire
      CM: '237', // Cameroun
    };

    // Extraire l'indicatif et le numéro
    let indicative = '';
    let phoneNumber = '';

    // Essayer de trouver l'indicatif dans la chaîne
    for (const [country, code] of Object.entries(countryCodes)) {
      if (fullPhoneNumber.includes(code)) {
        const parts = fullPhoneNumber.split(code);
        if (parts.length > 1) {
          indicative = code;
          phoneNumber = parts[1].replace(/[^0-9]/g, ''); // Garder seulement les chiffres
          break;
        }
      }
    }

    // Si aucun indicatif trouvé, essayer une approche différente
    if (!indicative) {
      // Supprimer tous les caractères non numériques sauf le +
      const cleanNumber = fullPhoneNumber.replace(/[^\d+]/g, '');

      // Essayer de trouver un indicatif de 2-3 chiffres
      const match = cleanNumber.match(/^\+?(\d{2,3})(\d+)$/);
      if (match) {
        indicative = match[1];
        phoneNumber = match[2];
      }
    }

    return { indicative, phoneNumber };
  }

  // Navigation entre les étapes
  goBack(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  // Réinitialiser le processus
  resetProcess(): void {
    this.currentStep = 1;
    this.otpId = '';
    this.phoneNumber = '';
    this.indicative = '';
    this.step1Form.reset();
    this.step2Form.reset();
    this.step3Form.reset();
  }

  closeModal(): void {
    this.modalService.closeAll();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Getters pour les erreurs de validation
  get step1PhoneNumberError(): string {
    const control = this.step1Form.get('phoneNumber');
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Le numéro de téléphone est requis';
      }
    }
    return '';
  }

  get step2OtpError(): string {
    const control = this.step2Form.get('otpCode');
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Le code OTP est requis';
      }
      if (control.errors['minlength'] || control.errors['maxlength']) {
        return 'Le code OTP doit contenir 6 chiffres';
      }
    }
    return '';
  }

  get step3NewPasswordError(): string {
    const control = this.step3Form.get('newPassword');
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Le nouveau mot de passe est requis';
      }
      if (control.errors['minlength']) {
        return 'Le mot de passe doit contenir au moins 8 caractères';
      }
      if (control.errors['pattern']) {
        return 'Le mot de passe doit contenir au moins une minuscule, une majuscule, un chiffre et un caractère spécial';
      }
    }
    return '';
  }

  get step3ConfirmPasswordError(): string {
    const control = this.step3Form.get('confirmPassword');
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'La confirmation du mot de passe est requise';
      }
    }
    return '';
  }

  get passwordsMatchError(): string {
    const newPassword = this.step3Form.get('newPassword')?.value;
    const confirmPassword = this.step3Form.get('confirmPassword')?.value;

    if (
      newPassword &&
      confirmPassword &&
      newPassword !== confirmPassword &&
      this.step3Form.get('confirmPassword')?.touched
    ) {
      return 'Les mots de passe ne correspondent pas';
    }
    return '';
  }
}
